// features/profile/edit/MoreCards.tsx — Edit Profile: Live Profile Preview, Profile
// Appearance, Privacy & Discoverability, Interests & Tags, Verification & Trust,
// and the closing motto card (1.27.70, owner concept edit_profile_v2).
//
// Coming soon (owner ruling 2026-09-30): header style, background effects, "Show
// in search results" and "Show content label". They render disabled with the chip.
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Switch } from '@/ui';
import { Icon } from '@/icons';
import { useTopics } from '@/hooks';
import {
  AVATAR_KEYS,
  BANNER_TONES,
  avatarGradient,
  bannerGradient,
} from '@/theme/presets/identityGradients';
import type { User } from '@/core/models/account';
import { InterestPicker } from '@/features/onboarding/InterestPicker';
import {
  CardHead,
  ComingSoon,
  DashCard,
  DashLinkRow,
  DashRow,
  Pill,
} from '@/features/settings/dashboard/DashCard';
import { RowList } from '@/features/settings/dashboard/dashStyles';
import type { Audience, ProfileDraftState } from './useProfileDraft';
import { ProfilePreview } from './ProfilePreview';
import { Select } from './fieldStyles';

/* A Coming soon chip set beside the row's label, so the trailing slot keeps only
   the (disabled) switch and the label keeps its width in a narrow card. */
const SoonLabel = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
`;

// ─── Live Profile Preview ────────────────────────────────────────────────────

const ViewFull = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const PreviewBody = styled.div`
  margin-top: 16px;
`;

export function PreviewCard({ previewUser }: { previewUser: User }) {
  return (
    <DashCard labelledBy="ep-preview-title" testId="preview-card">
      <CardHead
        icon="eye"
        title="Live Profile Preview"
        titleId="ep-preview-title"
        divided={false}
        aside={
          <ViewFull to="/profile">
            View full profile
            <Icon name="external-link" size={12} />
          </ViewFull>
        }
      />
      <PreviewBody>
        <ProfilePreview user={previewUser} />
      </PreviewBody>
    </DashCard>
  );
}

// ─── Profile Appearance ──────────────────────────────────────────────────────

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 14px;
`;

const GroupLabel = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

/* Selected = a soft ring in the text colour plus a check, never a bright outline. */
const selectedRing = css`
  box-shadow:
    0 0 0 2px ${({ theme }) => theme.color.surface},
    0 0 0 4px color-mix(in srgb, ${({ theme }) => theme.color.textPrimary} 55%, transparent);
`;

const Swatch = styled.button<{ $bg: string; $selected: boolean; $wide?: boolean }>`
  position: relative;
  width: ${({ $wide }) => ($wide ? '56px' : '32px')};
  height: 32px;
  border: 0;
  border-radius: ${({ theme, $wide }) => ($wide ? theme.radius.sm : '50%')};
  background: ${({ $bg }) => $bg};
  cursor: pointer;
  display: inline-grid;
  place-items: center;
  color: #fff;
  ${({ $selected }) => $selected && selectedRing}
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 4px;
  }
`;

const HeaderStyles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const StyleTile = styled.span<{ $bg: string }>`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 52px;
  padding-bottom: 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $bg }) => $bg};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  opacity: 0.55;
`;

/** The Coming soon switches: shown, labelled, and inert on purpose. */
const notYet = () => undefined;

export function AppearanceCard({ state }: { state: ProfileDraftState }) {
  const { draft, set, setMedia, hasAvatarImage, hasBannerImage } = state;
  return (
    <DashCard labelledBy="ep-appearance-title" id="appearance" testId="appearance-card">
      <CardHead
        icon="palette"
        title="Profile Appearance"
        titleId="ep-appearance-title"
        subtitle="Customize the look of your profile."
      />
      <Group>
        <GroupLabel id="ep-avatar-colour">Photo colour</GroupLabel>
        <Swatches role="group" aria-labelledby="ep-avatar-colour">
          {AVATAR_KEYS.map((key, i) => {
            const selected = !hasAvatarImage && draft.avatarKey === key;
            return (
              <Swatch
                key={key}
                type="button"
                $bg={avatarGradient(key)}
                $selected={selected}
                aria-pressed={selected}
                aria-label={`Photo colour ${i + 1}${hasAvatarImage ? ', replaces your photo' : ''}`}
                onClick={() => {
                  set('avatarKey', key);
                  if (hasAvatarImage)
                    setMedia((m) => ({ ...m, avatarFile: null, clearAvatarImage: true }));
                }}
              >
                {selected && <Icon name="check" size={14} />}
              </Swatch>
            );
          })}
        </Swatches>
      </Group>
      <Group>
        <GroupLabel id="ep-banner-colour">Banner colour</GroupLabel>
        <Swatches role="group" aria-labelledby="ep-banner-colour">
          {BANNER_TONES.map((tone, i) => {
            const selected = !hasBannerImage && draft.bannerTone === tone;
            return (
              <Swatch
                key={tone}
                type="button"
                $wide
                $bg={bannerGradient(tone)}
                $selected={selected}
                aria-pressed={selected}
                aria-label={`Banner colour ${i + 1}${hasBannerImage ? ', replaces your banner image' : ''}`}
                onClick={() => {
                  set('bannerTone', tone);
                  if (hasBannerImage)
                    setMedia((m) => ({ ...m, bannerFile: null, clearBannerImage: true }));
                }}
              >
                {selected && <Icon name="check" size={14} />}
              </Swatch>
            );
          })}
        </Swatches>
      </Group>
      <Group>
        <GroupLabel>
          Header style <ComingSoon />
        </GroupLabel>
        <HeaderStyles aria-hidden="true">
          <StyleTile $bg={bannerGradient('violet')}>Default</StyleTile>
          <StyleTile $bg={bannerGradient('magenta')}>Gradient</StyleTile>
          <StyleTile $bg="transparent">Minimal</StyleTile>
        </HeaderStyles>
      </Group>
      <RowList>
        <DashRow
          icon="sparkles"
          label={
            <SoonLabel>
              Background effects <ComingSoon />
            </SoonLabel>
          }
          hint="A subtle glow on your profile header"
        >
          <Switch checked={false} onChange={notYet} label="Background effects" disabled />
        </DashRow>
      </RowList>
    </DashCard>
  );
}

// ─── Privacy & Discoverability ───────────────────────────────────────────────

const AUDIENCE_LABELS: Record<Audience, string> = {
  everyone: 'Everyone',
  following: 'People I follow',
  nobody: 'Nobody',
};

const SmallSelect = styled(Select)`
  width: auto;
  min-height: 32px;
  padding: 5px 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

export function PrivacyCard({ state }: { state: ProfileDraftState }) {
  const { draft, set } = state;
  return (
    <DashCard labelledBy="ep-privacy-title" id="privacy" testId="privacy-card">
      <CardHead
        icon="lock"
        title="Privacy & Discoverability"
        titleId="ep-privacy-title"
        subtitle="Control who can find and interact with you."
      />
      <RowList>
        <DashRow
          icon="globe"
          label="Public profile"
          hint={
            draft.privateAccount
              ? 'Only your followers see your posts'
              : 'Anyone can see your posts'
          }
        >
          <Switch
            checked={!draft.privateAccount}
            onChange={(next) => set('privateAccount', !next)}
            label="Public profile"
          />
        </DashRow>
        <DashRow icon="mail" label="Allow direct messages" hint="Who can message you">
          <SmallSelect
            aria-label="Who can message you"
            value={draft.dmPolicy}
            onChange={(e) => set('dmPolicy', e.target.value as Audience)}
          >
            {(Object.keys(AUDIENCE_LABELS) as Audience[]).map((a) => (
              <option key={a} value={a}>
                {AUDIENCE_LABELS[a]}
              </option>
            ))}
          </SmallSelect>
        </DashRow>
        <DashRow
          icon="search"
          label={
            <SoonLabel>
              Show in search results <ComingSoon />
            </SoonLabel>
          }
          hint="Help others find you on TruXSocial"
        >
          <Switch checked={false} onChange={notYet} label="Show in search results" disabled />
        </DashRow>
        <DashRow
          icon="briefcase"
          label={
            <SoonLabel>
              Show content label <ComingSoon />
            </SoonLabel>
          }
          hint="Display your creator type on your profile"
        >
          <Switch checked={false} onChange={notYet} label="Show content label" disabled />
        </DashRow>
      </RowList>
    </DashCard>
  );
}

// ─── Interests & Tags ────────────────────────────────────────────────────────

const InterestBody = styled.div`
  padding-top: 14px;
`;

const Small = styled.p`
  margin: 10px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function InterestsCard({ state }: { state: ProfileDraftState }) {
  const { draft, set } = state;
  const { topics, loading } = useTopics();
  return (
    <DashCard labelledBy="ep-interests-title" id="interests" testId="interests-card">
      <CardHead
        icon="hash"
        title="Interests & Tags"
        titleId="ep-interests-title"
        subtitle="Topics you are into."
      />
      <InterestBody>
        {!loading && (
          <InterestPicker
            topics={topics}
            value={draft.interests}
            onChange={(next) => set('interests', next)}
            label="Your interests"
          />
        )}
        <Small>Only you see these. They shape your For You feed and who we suggest to you.</Small>
      </InterestBody>
    </DashCard>
  );
}

// ─── Verification & Trust ────────────────────────────────────────────────────

export function VerificationCard({ user }: { user: User }) {
  return (
    <DashCard labelledBy="ep-verify-title" id="verification" testId="verification-card">
      <CardHead
        icon="shield"
        title="Verification & Trust"
        titleId="ep-verify-title"
        subtitle="What backs up your account."
      />
      <RowList>
        <DashRow icon="mail" label="Email verification">
          {user.emailVerified ? (
            <Pill tone="success" dot>
              Verified
            </Pill>
          ) : (
            <Pill>Not yet</Pill>
          )}
        </DashRow>
        <DashRow icon="smartphone" label="Two-factor sign-in">
          {user.twoFactorEnabled ? (
            <Pill tone="success" dot>
              On
            </Pill>
          ) : (
            <Pill>Off</Pill>
          )}
        </DashRow>
        <DashRow
          icon="verify"
          label="Verified badge"
          hint="Earned, never bought. It needs two-factor sign-in."
        >
          {user.verified ? (
            <Pill tone="accent" dot>
              Verified
            </Pill>
          ) : (
            <Pill>Not verified</Pill>
          )}
        </DashRow>
      </RowList>
      <RowList>
        <DashLinkRow to="/settings/account" icon="settings" label="Manage in Account Settings" />
      </RowList>
    </DashCard>
  );
}

// ─── Motto ───────────────────────────────────────────────────────────────────

const MottoRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  padding: 24px 8px;
  > svg {
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const MottoLine = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight.lg};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MottoSub = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.md};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function MottoCard() {
  return (
    <DashCard labelledBy="ep-motto-title" tone="accent" testId="motto-card">
      <MottoRoot>
        <Icon name="crown" size={36} />
        <MottoLine id="ep-motto-title">A more open, authentic social experience.</MottoLine>
        <MottoSub>
          Real people.
          <br />
          Real conversations.
          <br />
          Always you.
        </MottoSub>
      </MottoRoot>
    </DashCard>
  );
}
